import React, { useContext, useMemo } from 'react'
import { OverrideContext } from './overrideContext'
import { isShopCartDefault } from '../utils/cartTools'
import type { QueryParamContextValue } from './queryParamOverrides'
import {
    ApiShopperLandingPage,
    IS_EDITOR_PREVIEW_PARAM,
    IS_PREVIEW_PARAM,
    NO_PIXELS_PARAM,
    NO_PREVIEW_BANNER_PARAM,
    PREVIEW_HOSTNAME,
} from '@storefronts/common-types'

export type { QueryParamOverrideInput } from './queryParamOverrides'

export const findInQueryParams = (key: string): boolean => {
    if (typeof window === 'undefined') return false
    const params = new URLSearchParams(window.location.search)
    if (params.get(key)) return true
    return false
}

export const IS_DEVELOPMENT = (): boolean => {
    const isProduction = GET_PARAM('is-production')
    // isProduction === 'true' seems to be the opposite of what's intended.
    // But that was the original implementation. Not sure if this is used anywhere.
    return !isProduction || isProduction === 'true'
}

export const DISCOUNT_CODE = (): string | null => {
    return GET_PARAM('discount') ?? GET_PARAM('discount-code')
}

export const ALL_PARAMS = (): Record<string, string> => {
    if (typeof window === 'undefined') return {}
    const params = new URLSearchParams(window.location.search)
    const obj: Record<string, string> = {}
    for (const [key, value] of params.entries()) {
        obj[key] = value
    }
    return obj
}

export const HIDE_CART_OVERRIDE = (): boolean => {
    return GET_PARAM('hide-cart') === 'true'
}

export const VARIANT_ID = (): string | null => {
    return GET_PARAM('variant')
}

export const GET_PARAM = (param: string): string | null => {
    if (findInQueryParams(param)) {
        const params = new URLSearchParams(window.location.search)
        return params.get(param)
    }
    return null
}

export const IS_EDITOR_PREVIEW = (): boolean =>
    GET_PARAM(IS_EDITOR_PREVIEW_PARAM) === 'true'

export const IS_NO_PIXELS = (): boolean => GET_PARAM(NO_PIXELS_PARAM) === 'true'

export const HIDE_PREVIEW_BANNER = (): boolean =>
    GET_PARAM(NO_PREVIEW_BANNER_PARAM) === 'true'

export const IS_PREVIEW = (): boolean => {
    if (typeof window !== 'undefined') {
        const isPreviewParam = GET_PARAM(IS_PREVIEW_PARAM)
        if (isPreviewParam !== null) {
            return isPreviewParam === 'true'
        }
        return window.location.hostname.includes(PREVIEW_HOSTNAME)
    }
    return false
}

const defaultQueryParamContext: QueryParamContextValue = {
    IS_DEVELOPMENT: IS_DEVELOPMENT(),
    DISCOUNT_CODE: DISCOUNT_CODE(),
    HIDE_CART_OVERRIDE: HIDE_CART_OVERRIDE(),
    VARIANT_ID: VARIANT_ID(),
    EVENT_ID: GET_PARAM('eventId'),
    P_EVENT_ID: GET_PARAM('pEventId'),
    SHOP: GET_PARAM('shop'),
    IS_PREVIEW: IS_PREVIEW(),
    SHOP_CART: GET_PARAM('shopCart') === 'true',
    SHARE_CART: GET_PARAM('shareCart') === 'true',
}

export const QueryParamContext = React.createContext(defaultQueryParamContext)

export const QueryParamContextProvider: React.FC<{
    children: React.ReactNode
    landingPage: ApiShopperLandingPage
}> = ({ children, landingPage }) => {
    const { qpOverrides } = useContext(OverrideContext)
    const shopCart = isShopCartDefault(landingPage)
        ? !(GET_PARAM('shopCart') === 'false')
        : GET_PARAM('shopCart') === 'true'

    // for now, default shareCart based on shopCart
    const shareCartParam = GET_PARAM('shareCart') ?? `${shopCart}`
    const shareCart = shareCartParam === 'true'

    // eslint-disable-next-line complexity
    const queryParamContext: QueryParamContextValue = useMemo(() => {
        let discountCode: string | null = null
        if (qpOverrides && qpOverrides.DISCOUNT_CODE) {
            discountCode = qpOverrides.DISCOUNT_CODE
        }
        if (DISCOUNT_CODE()) {
            discountCode = DISCOUNT_CODE() as string
        }

        const params: QueryParamContextValue = {
            IS_DEVELOPMENT:
                qpOverrides && qpOverrides.IS_DEVELOPMENT
                    ? qpOverrides.IS_DEVELOPMENT
                    : IS_DEVELOPMENT(),
            DISCOUNT_CODE: discountCode,
            HIDE_CART_OVERRIDE:
                qpOverrides && qpOverrides.HIDE_CART_OVERRIDE
                    ? qpOverrides.HIDE_CART_OVERRIDE
                    : HIDE_CART_OVERRIDE(),
            VARIANT_ID:
                qpOverrides && qpOverrides.VARIANT_ID
                    ? qpOverrides.VARIANT_ID
                    : VARIANT_ID(),
            EVENT_ID:
                qpOverrides && qpOverrides.EVENT_ID
                    ? qpOverrides.EVENT_ID
                    : GET_PARAM('eventId'),
            P_EVENT_ID:
                qpOverrides && qpOverrides.P_EVENT_ID
                    ? qpOverrides.P_EVENT_ID
                    : GET_PARAM('pEventId'),
            SHOP:
                qpOverrides && qpOverrides.SHOP
                    ? qpOverrides.SHOP
                    : GET_PARAM('shop'),
            IS_PREVIEW:
                qpOverrides && qpOverrides.IS_PREVIEW !== undefined
                    ? qpOverrides.IS_PREVIEW
                    : IS_PREVIEW(),
            SHOP_CART:
                qpOverrides && qpOverrides.SHOP_CART
                    ? qpOverrides.SHOP_CART
                    : shopCart,
            SHARE_CART:
                qpOverrides && qpOverrides.SHARE_CART
                    ? qpOverrides.SHARE_CART
                    : shareCart,
        }

        return params
    }, [qpOverrides, shopCart, shareCart])
    return (
        <QueryParamContext.Provider value={queryParamContext}>
            {children}
        </QueryParamContext.Provider>
    )
}
